import React, { useState } from "react";
import { CicdFaqs } from "../../../components/faqData.js";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const AzureSolutions = () => {
  const [actIndex, setActIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActIndex(index === actIndex ? null : index);
  };
  const scrollToContact = () => {
    document
      .getElementById("contact-section")
      .scrollIntoView({ behavior: "smooth" });
  };

  const azureOfferings = [
    {
      logo_images:"../assets/icons/azure-solution/service-1.png",
      title: "Azure Virtual Machines",
      description:
        "Deploy Windows and Linux virtual machines with Azure VMs, offering flexible sizing options, high availability, and seamless integration with Azure services.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-2.png",
      title: "Azure Blob Storage",
      description:
        "Store massive amounts of unstructured data with Azure Blob Storage, featuring tiered storage options, global redundancy, and enterprise-grade security.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-3.png",
      title: "Azure Networking (VNet)",
      description:
        "Create isolated, secure network environments with Azure Virtual Network, enabling private connectivity, VPN gateways, and advanced traffic management.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-4.png",
      title: "Azure Active Directory",
      description:
        "Manage identities and access with Azure AD, providing single sign-on, multi-factor authentication, and comprehensive identity governance for hybrid environments.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-5.png",
      title: "Azure SQL & Cosmos DB",
      description:
        "Deploy managed SQL databases and globally distributed NoSQL databases with Azure SQL Database and Cosmos DB, featuring automatic scaling and high availability.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-6.png",
      title: "Azure Functions (Serverless)",
      description:
        "Build event-driven, serverless applications with Azure Functions, enabling code execution without infrastructure management and automatic scaling.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-7.png",
      title: "Azure Kubernetes Service (AKS)",
      description:
        "Orchestrate containerized applications with AKS, providing managed Kubernetes clusters with simplified deployment, scaling, and management capabilities.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-8.png",
      title: "Azure DevOps Pipelines",
      description:
        "Automate CI/CD workflows with Azure DevOps Pipelines, enabling continuous integration, testing, and deployment across multiple environments and platforms.",
    },
  ];

  const benefits = [
    "Hybrid cloud capabilities for seamless on-premises integration",
    "Enterprise-grade security with built-in compliance certifications",
    "Cost-effective pricing with Azure Reserved Instances and Hybrid Benefit",
    "Global presence with 60+ regions and availability zones",
    "Comprehensive integration with Microsoft ecosystem (Office 365, Dynamics)",
    "Advanced AI and machine learning services with Azure AI",
  ];

  const expertise = [
    "Microsoft Azure Certified Solutions Architects and DevOps Engineers",
    "Hybrid cloud architecture design and implementation",
    "Azure Active Directory and identity management solutions",
    "Container orchestration with AKS and Azure Container Instances",
    "Serverless architecture using Azure Functions and Logic Apps",
    "CI/CD pipeline automation with Azure DevOps and GitHub Actions",
    "Infrastructure as Code with ARM templates and Terraform",
    "Cost optimization strategies and Azure Cost Management",
  ];

  const useCases = [
    {
      title: "Enterprise Hybrid Cloud Migration",
      description:
        "Migrated on-premises Windows Server infrastructure to Azure, achieving 35% cost reduction while maintaining seamless integration with existing Active Directory and Office 365 environments.",
    },
    {
      title: "Scalable Web Application Platform",
      description:
        "Built a serverless microservices architecture using Azure Functions, API Management, and Cosmos DB, handling peak traffic of 10M+ requests per day with auto-scaling.",
    },
    {
      title: "Multi-Region High Availability",
      description:
        "Implemented Azure SQL Database with geo-replication and Azure Traffic Manager, achieving 99.99% uptime across multiple regions with automatic failover capabilities.",
    },
    {
      title: "DevOps Transformation",
      description:
        "Established end-to-end CI/CD pipelines with Azure DevOps, reducing deployment cycles from weeks to hours and enabling automated testing, security scanning, and rollback capabilities.",
    },
  ];

  const whyChooseUs = [
    "Microsoft Azure certified partners with proven migration expertise",
    "Deep integration knowledge of Microsoft ecosystem and hybrid cloud",
    "End-to-end services from architecture design to managed operations",
    "Cost optimization strategies reducing Azure spend by 25-40%",
    "24/7 monitoring with Azure Monitor and Application Insights",
    "Security-first approach with Azure Security Center and compliance",
  ];

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Accelerate growth with our Azure solutions for enterprise cloud transformation. Secure, scalable, and tailored cloud strategies to drive your business forward."
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Azure Solutions for Enterprise Cloud Transformation | Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-azure"
        />
        <meta
          property="og:description"
          content="Accelerate growth with our Azure solutions for enterprise cloud transformation. Secure, scalable, and tailored cloud strategies to drive your business forward."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Azure Solutions for Enterprise Cloud Transformation | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Accelerate growth with our Azure solutions for enterprise cloud transformation. Secure, scalable, and tailored cloud strategies to drive your business forward."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <link
          rel="canonical"
          href="http://ayanshtechnology.com/devops-azure"
        />
        <title>
          Azure Solutions for Enterprise Cloud Transformation | Ayanshtech Solutions
        </title>
      </Helmet>
      <div className="flex flex-col items-center justify-center mb-6 p-6">
        {/* Hero Section */}
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Azure Solutions for Enterprise Cloud Transformation
              </h1>

              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Accelerate your digital transformation with Microsoft Azure, the
                enterprise cloud platform trusted by Fortune 500 companies
                worldwide. Ayanshtech Solutions delivers comprehensive Azure
                consulting, migration, and managed services to help you leverage
                the full power of Microsoft's cloud ecosystem.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Our Azure-certified experts design, deploy, and optimize cloud
                architectures that seamlessly integrate with your existing
                Microsoft infrastructure. From Virtual Machines and Blob Storage
                to serverless Functions and Kubernetes orchestration, we help you
                build secure, scalable, and cost-effective cloud solutions.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Whether you're migrating from on-premises systems, building
                cloud-native applications, or optimizing existing Azure
                infrastructure, our team ensures seamless implementation with
                best practices, security compliance, and cost optimization at
                the core of every solution.
              </p>

              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Get Started
              </button>
            </div>

            <div className="flex w-full md:w-1/2 justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/azure-banner.png"
                alt="Azure Cloud Solutions"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* About Microsoft Azure Cloud */}
        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            About Microsoft Azure Cloud
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Microsoft Azure is a comprehensive cloud computing platform offering
            over 200 services spanning compute, storage, networking, databases,
            analytics, AI, and IoT. With a global presence across 60+ regions,
            Azure provides enterprise-grade security, compliance, and hybrid
            cloud capabilities that seamlessly integrate with existing Microsoft
            ecosystems.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            At Ayanshtech, we specialize in helping enterprises leverage Azure's
            capabilities to modernize infrastructure, accelerate digital
            transformation, and achieve operational excellence. Our comprehensive
            Azure services cover everything from initial strategy and migration
            to ongoing optimization and managed operations, with deep expertise
            in hybrid cloud scenarios and Microsoft integration.
          </p>
        </div> */}

        {/* Azure Offerings */}
        <div className="text-center mb-5 mt-12">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Comprehensive Azure Offerings
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Explore our extensive range of Azure services designed to meet your
            infrastructure, application, and business requirements.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {azureOfferings.map((offering, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >

                  <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={offering.logo_images} className="w-full " alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{offering.title}</h3>
                  </div>
                  <p className="text-lg">{offering.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits of Azure */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Benefits of Azure for Enterprises
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Discover how Microsoft Azure cloud services can transform your
            enterprise operations, reduce costs, and accelerate innovation.
          </p>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Our Expertise in Azure */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Our Azure Expertise
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Ayanshtech's Azure-certified team brings deep expertise across the
              entire Microsoft Azure ecosystem, delivering solutions that align
              with your business objectives and technical requirements.
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center item-center mb-8">
            <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-7xl my-auto space-y-3">
              {expertise.map((item, index) => (
                <li key={index} className="text-gray-700">
                  {item}
                </li>
              ))}
            </ul>
            <div className="w-full md:w-1/2 lg:w-[40%] mx-auto">
              <img src="https://www.flatworldedge.com/images/azur-mig/banner-azur-migration-service.png" alt="" />
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Real-World Use Cases
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            See how we've helped enterprises achieve their cloud transformation
            goals with Azure solutions.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold">{useCase.title}</h3>
                  <p className="text-lg">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why Choose Ayanshtech for Azure Solutions
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Partner with Ayanshtech for Azure expertise that drives measurable
              business outcomes. Our proven methodology, certified engineers, and
              customer-first approach ensure successful cloud transformations.
            </p>
          </div>

          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 rounded-2xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={scrollToContact}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule a Free Azure Consultation Today!
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto p-6 py-20 w-full">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-8">
            FAQs
          </h2>
          {CicdFaqs.map((faq, index) => (
            <div key={index} className="border-b">
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full text-left py-4 flex justify-between items-center focus:outline-none ${actIndex === index
                    ? "bg-blue-600 text-white px-5 rounded-2xl"
                    : ""
                  }`}
              >
                <span className="text-lg">{faq.question}</span>
                <span className="text-2xl">
                  {actIndex === index ? "-" : "+"}
                </span>
              </button>
              {actIndex === index && (
                <div className="p-4 text-gray-700">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div id="contact-section">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default AzureSolutions;

